/**
 * @hf/audio — WebAudio engine: buses (master/music/sfx/crowd/announcer), ducking, stereo pan, a ZzFX procedural
 * SFX bank, and players for music / announcer barks / crowd loops (ARCHITECTURE §2).
 * Depends on @hf/shared only. Knows nothing about the sim: the client's audio-bridge maps SimEvents to calls.
 *
 * This file defines the PUBLIC contract. `createAudioEngine` (engine.ts) is the WebAudio implementation;
 * `createSilentAudioEngine` is the no-op used by tests, servers and runtimes without WebAudio.
 */
export type AudioBus = 'master' | 'music' | 'sfx' | 'crowd' | 'announcer';
export const AUDIO_BUSES: readonly AudioBus[] = ['master', 'music', 'sfx', 'crowd', 'announcer'];

/** 0..1 per bus. */
export type AudioVolumes = Record<AudioBus, number>;
export const DEFAULT_VOLUMES: Readonly<AudioVolumes> = {
  master: 0.8,
  music: 0.6,
  sfx: 0.9,
  crowd: 0.7,
  announcer: 1,
};

export interface PlayOptions {
  /** 0..1 multiplier on top of the bus volume (values up to 2 are allowed for emphasis). */
  volume?: number;
  /** Playback-rate multiplier (1 = normal). The bank's random pitch variation is applied on top. */
  pitch?: number;
  /** Stereo pan -1 (left) .. 1 (right). The bridge derives it from world x relative to the camera. */
  pan?: number;
}

export interface AnnounceOptions {
  /**
   * Higher interrupts lower; equal priority queues; lower is dropped while a line is speaking (unless `chain`, or
   * an explicit `wait` the booth will be free within). Default 1.
   */
  priority?: number;
  /** Duck music/crowd while speaking (default true). */
  duck?: boolean;
  /**
   * The line belongs to the one(s) already speaking/queued ("HE'S ON FIRE!!" right after "TOUCHDOWN!!!", the
   * versus intro): its queue timeout starts when the lines ahead finish instead of now, so a long line ahead can
   * never make it expire, and a chained line queues even behind a higher-priority one (unchained lower lines are
   * dropped). Unchained queued lines expire ~1.6 s after they were requested (stale barks).
   */
  chain?: boolean;
  /**
   * Unchained: how long (s) the line may wait in the queue for the booth before it is stale (default
   * `QUEUE_TTL_S`, 1.6 s). A play's result ("SACKED!!" behind "BLITZ!! Here comes Grandpa!") is still news ~2.5 s
   * later; a colour bark is not. Given explicitly, it also lets a LOWER-priority line queue behind the current one
   * when that is projected to finish within it (instead of being dropped).
   */
  wait?: number;
}

/** Asset manifest entry for file-based audio (apps/client/public/audio/audio-manifest.json). */
export interface AudioAsset {
  id: string;
  url: string;
  bus: AudioBus;
  loop?: boolean;
  /** Caption text for announcer lines (HUD captions). */
  caption?: string;
  /** Linear trim applied on top of the bus (default 1). */
  gain?: number;
  /** Duration in seconds (informative: caption timing before the file is decoded). */
  duration?: number;
  /** Loops: where repeats restart (s, default 0). The first pass plays from 0, so an intro plays once. */
  loopStart?: number;
  /** Loops: where the next repeat begins (s, default duration − loopXfade). */
  loopEnd?: number;
  /**
   * Announcer lines: phrase breaks (s from the start of the file) where a pause between two phrases begins
   * (informative, like `duration`: the engine measures the decoded file itself; tools and tests read these).
   */
  breaks?: number[];
  /** Loops: equal-power crossfade at the seam in seconds (default 1.5, clamped to the buffer). */
  loopXfade?: number;
}

/** `AudioEngine.load` options. */
export interface LoadOptions {
  /**
   * Register only (captions and durations are known at once); fetch nothing until `prefetch` or a first use
   * (a deferred line asked for before it loaded shows its caption silently and loads for next time).
   */
  defer?: boolean;
}

/** A line the announcer started speaking (or would have, when audio is locked/muted: captions still show). */
export interface AnnouncedLine {
  id: string;
  caption: string;
  durationMs: number;
}

export type CrowdReaction = 'cheer' | 'gasp' | 'boo' | 'roar' | 'ooh';

/** Debug/test snapshot of the engine. */
export interface AudioStats {
  state: 'locked' | 'running' | 'suspended' | 'closed';
  /** Gameplay + UI SFX voices currently sounding. */
  voices: number;
  /** Decoded file assets / registered file assets. */
  loaded: number;
  registered: number;
  music: string | null;
  crowd: number;
  speaking: string | null;
  queued: number;
  /**
   * The master bus dynamics of the live context: WebAudio glue compressor + limiter, the soft-clip fallback (no
   * DynamicsCompressorNode, e.g. react-native-audio-api), none, or null before a context exists.
   */
  master?: 'compressor' | 'softclip' | 'direct' | null;
  /** Decoded PCM held by the engine (file assets + the SFX bank), bytes. */
  pcmBytes?: number;
}

/** One meter reading in dBFS (−120 = silence): RMS and peak over the analyser's last ~21 ms window. */
export interface LevelReading {
  rms: number;
  peak: number;
}
/** Live levels of every bus and of the output (`out`: what reaches the speaker, after the master dynamics). */
export type AudioLevels = Record<'out' | Exclude<AudioBus, 'master'>, LevelReading>;

export interface AudioEngine {
  /** True after `unlock()` succeeded (AudioContext running). */
  readonly unlocked: boolean;
  /** Call from the first user gesture (autoplay policy). Idempotent. */
  unlock(): Promise<void>;
  /** Preload file-based assets. Resolves when every non-music asset is decoded (music decodes on demand). */
  load(assets: readonly AudioAsset[], opts?: LoadOptions): Promise<void>;
  setVolumes(v: Partial<AudioVolumes>): void;
  getVolumes(): AudioVolumes;
  setMuted(muted: boolean): void;
  /** Fire a procedural or file SFX by id. Unknown ids are no-ops. */
  sfx(id: string, opts?: PlayOptions): void;
  /** Announcer bark by id. Returns the caption (if any) so the HUD can show it; null when unknown or dropped. */
  announce(id: string, opts?: AnnounceOptions): string | null;
  /** Crossfade to a music track (null = stop). Remembered while locked/loading and started when possible. */
  music(trackId: string | null, fadeMs?: number): void;
  /** Continuous crowd intensity 0..1 (bed loop volume/filter; 0 = no crowd); spikes via `crowdReact`. */
  crowd(intensity: number): void;
  /** One-shot crowd reaction (cheer, gasp, boo, roar). */
  crowdReact(kind: CrowdReaction, strength?: number): void;
  /** Pause/resume everything (tab hidden, app backgrounded). */
  suspend(): void;
  resume(): void;
  dispose(): void;

  // ── Optional extensions (feature-detect: `engine.onUnlock?.(…)`) ──────────────────────────────────────
  /** Called once when the context first runs (immediately if it already does). Returns an unsubscribe. */
  onUnlock?(cb: () => void): () => void;
  /**
   * Called when an announcer line starts (precise caption timing: a line scheduled ahead — a bigger call waiting
   * for the current line's phrase break — is reported when its first word sounds). Returns an unsubscribe.
   */
  onAnnounce?(cb: (line: AnnouncedLine) => void): () => void;
  /**
   * Pause menu: muffle gameplay SFX/crowd/announcer (UI sounds and music stay clear). `{ booth: false }` keeps the
   * announcer clear (instant replays: the stadium goes "to tape" while the booth calls the replay).
   */
  setPaused?(paused: boolean, opts?: { booth?: boolean }): void;
  /**
   * Called when an announcer line stops sounding: finished (`cut` false) or interrupted/hushed (`cut` true; a cut
   * at a phrase break ahead is reported when the voice actually stops). A line dropped before it ever sounded is
   * not reported (its start never was either).
   */
  onLineEnd?(cb: (id: string, cut: boolean) => void): () => void;
  /** Rhythmic crowd chant loop ("DE-FENSE!") on/off. */
  crowdChant?(on: boolean): void;
  /**
   * Leaving the stadium: silence the announcer (and its queue), fade out crowd reactions, gameplay SFX voices and
   * the chant over `fadeMs` (default 300). Music, UI sounds and the crowd beds (`crowd()`) are untouched.
   */
  hush?(fadeMs?: number): void;
  stats?(): AudioStats;
  /** Live bus/output meters when the engine was created with `meters: true`, else null. */
  levels?(): AudioLevels | null;
  /**
   * Load `defer`red assets now (all, or those `filter` picks). Resolves when they are decoded (music: fetched).
   * `urgent`: they jump the loading queue (ahead of a bulk prefetch), like an on-demand decode — for files a screen
   * is about to need (Football 101's coach lines once a lesson is starting).
   */
  prefetch?(filter?: (asset: AudioAsset) => boolean, opts?: { urgent?: boolean }): Promise<void>;
  /**
   * Drop an announcer line that has not made a sound yet (queued or scheduled behind the current line), e.g. the
   * match opener once the first snap happened. True if one was dropped. A line already speaking is untouched —
   * unless `fadeMs` is given: it is then faded out over that long (an instant replay skipped mid-narration), except
   * when it has at most `graceMs` left (default: its last word, ~450 ms), which lands.
   */
  cancelLine?(id: string, opts?: { fadeMs?: number; graceMs?: number }): boolean;
  /** App-set music level 0..1 (default 1) under the ducking, glided over `fadeMs` (default 600). */
  setMusicLevel?(level: number, fadeMs?: number): void;
  /**
   * Hand the engine bank sounds rendered elsewhere (the pre-rendered bank of `baked.ts`: native shells decode one WAV
   * instead of synthesising the bank in JS). Sounds already rendered are kept; unknown ids are ignored. Returns how
   * many were taken. Combine with `prerender: false`.
   */
  addRendered?(sounds: ReadonlyMap<string, { data: Float32Array; rate: number }>): number;
  /**
   * A match or a lesson is ticking (true) or the player is on the menus (false): background work (the SFX bank
   * pre-render) then only takes what idle time the frames leave and never overruns one.
   */
  setBusy?(busy: boolean): void;
}

/** A no-op engine for tests, servers and before unlock. */
export const createSilentAudioEngine = (): AudioEngine => {
  let volumes: AudioVolumes = { ...DEFAULT_VOLUMES };
  return {
    unlocked: false,
    unlock: async () => {},
    load: async () => {},
    setVolumes: (v) => {
      volumes = { ...volumes, ...v };
    },
    getVolumes: () => ({ ...volumes }),
    setMuted: () => {},
    sfx: () => {},
    announce: () => null,
    music: () => {},
    crowd: () => {},
    crowdReact: () => {},
    suspend: () => {},
    resume: () => {},
    dispose: () => {},
    onUnlock: () => () => {},
    onAnnounce: () => () => {},
    onLineEnd: () => () => {},
    setPaused: () => {},
    crowdChant: () => {},
    hush: () => {},
    prefetch: async () => {},
    cancelLine: () => false,
    setMusicLevel: () => {},
    setBusy: () => {},
    levels: () => null,
    stats: () => ({
      state: 'locked',
      voices: 0,
      loaded: 0,
      registered: 0,
      music: null,
      crowd: 0,
      speaking: null,
      queued: 0,
    }),
  };
};

export {
  createAudioEngine,
  masterChainFor,
  phraseBreaks,
  softClipCurveSamples,
  volumeToGain,
  trailingSilence,
  type AudioContextLike,
  type AudioEngineOptions,
  type MasterChain,
} from './engine';
export {
  BIG_BREAK_WAIT_S,
  BREAK_FADE_S,
  BREAK_WAIT_S,
  Booth,
  CHAIN_BREATH_S,
  CHAIN_LOOKAHEAD_S,
  INTERRUPT_ENTRY_S,
  INTERRUPT_FADE_S,
  INTERRUPT_GRACE_S,
  MAX_QUEUE,
  QUEUE_TTL_S,
  type BoothSources,
} from './announcer';
export { SFX_BANK, SFX_IDS, sfxDef, type SfxDef, type SfxId, type ZzfxLayer } from './sfx-bank';
export { renderSfx, sfxDuration } from './render';
export {
  handleSfxRenderRequest,
  serveSfxRenderer,
  type RenderWorkerLike,
  type RenderWorkerScope,
  type SfxRenderReply,
  type SfxRenderRequest,
} from './render-worker';
export { buildZzfxSamples, type ZzfxParams } from './zzfx';
export { adaptAudioContext } from './context-adapter';
export {
  interruptibleVisibility,
  type AppVisibilitySource,
  type InterruptibleVisibility,
} from './interruptions';
export { VoicePool } from './voices';
export { MUSIC_CACHE, type AssetStoreOptions, type FileDecoder } from './assets';
export { bakeSfxBank, encodeWav16, resampleLinear, sliceBakedBank, type BakedSfxIndex, type RenderedSound } from './baked';
